import React, { useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { layout, right, left, space } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { Icon } from '../icon/Icon';

const StyledInput = styled.div`
  ${layout}
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
`;

const HtmlInput = styled.input`
  font-size: ${themeGet('input.fontSize')};
  width: 100%;
  outline: none;
  box-sizing: border-box;
  height: ${props => themeGet(`input.sizes.${props.size}`)};
  border-top: ${themeGet('input.border.top')};
  border-right: ${themeGet('input.border.right')};
  border-bottom: ${themeGet('input.border.bottom')};
  border-left: ${themeGet('input.border.left')};
  border-radius: ${themeGet('input.borderRadius')};
  background-color: ${themeGet('input.backgroundColor')};
  ${space}
  ${layout}

  &::placeholder {
    color: ${props =>
      props.disabled ? themeGet('input.disabled.placeHolderColor') : themeGet('input.placeHolderColor')};
  }

  &:disabled {
    cursor: not-allowed;
    user-select: none;
    color: ${themeGet('input.disabled.color')};
  }

  &:hover {
    ${props =>
      !props.error &&
      !props.disabled &&
      css`
        border-top: ${themeGet('input.hover.border.top')};
        border-right: ${themeGet('input.hover.border.right')};
        border-bottom: ${themeGet('input.hover.border.bottom')};
        border-left: ${themeGet('input.hover.border.left')};
        background-color: ${themeGet('input.hover.backgroundColor')};
      `}
  }

  &:focus-within {
    ${props =>
      !props.error &&
      css`
        border-top: ${themeGet('input.focus.border.top')};
        border-right: ${themeGet('input.focus.border.right')};
        border-bottom: ${themeGet('input.focus.border.bottom')};
        border-left: ${themeGet('input.focus.border.left')};
        background-color: ${themeGet('input.focus.backgroundColor')};
        outline: none;
      `}
  }

  ${props =>
    props.disabled &&
    css`
      border-top: ${themeGet('input.disabled.border.top')};
      border-right: ${themeGet('input.disabled.border.right')};
      border-bottom: ${themeGet('input.disabled.border.bottom')};
      border-left: ${themeGet('input.disabled.border.left')};
      background-color: ${themeGet('input.disabled.backgroundColor')};
    `}

  ${props =>
    props.error &&
    css`
      border-top: ${themeGet('input.error.border.top')};
      border-right: ${themeGet('input.error.border.right')};
      border-bottom: ${themeGet('input.error.border.bottom')};
      border-left: ${themeGet('input.error.border.left')};
      background-color: ${themeGet('input.error.backgroundColor')};
    `}
`;

const ClearButton = styled(Icon).attrs({
  name: 'DELETE',
})`
  cursor: pointer;

  &:hover {
    color: ${themeGet('input.clearButton.hover.color')};
  }

  &:active {
    color: ${themeGet('input.clearButton.active.color')};
  }
`;

export const IconWrap = styled.span`
  display: flex;
  position: absolute;
  ${right}
  ${left}
`;

HtmlInput.defaultProps = {
  size: 'default',
};

export const Input = React.forwardRef(
  (
    { size, disabled, error, width, prefix, suffix, allowClear, value, defaultValue, onChange, ...props },
    ref
  ) => {
    const [currentValue, setCurrentValue] = useState(value || defaultValue);
    const onTextChange = useCallback(
      event => {
        if (typeof onChange === 'function') {
          onChange(event);
        }

        if (value === undefined) {
          setCurrentValue(event.target.value || '');
        }
      },
      [onChange, setCurrentValue, value]
    );

    if (value !== undefined && value !== currentValue) {
      setCurrentValue(value);
    }

    return (
      <StyledInput width={width}>
        <IconWrap left="8px">{prefix}</IconWrap>
        <HtmlInput
          py={0}
          pl={prefix ? '32px' : '8px'}
          pr={suffix || allowClear ? '32px' : '8px'}
          size={size}
          disabled={disabled}
          error={error}
          {...props}
          onChange={onTextChange}
          value={currentValue}
          ref={ref}
        />
        <IconWrap right="8px">{allowClear ? <ClearButton onClick={onTextChange} /> : suffix}</IconWrap>
      </StyledInput>
    );
  }
);

Input.defaultProps = {
  allowClear: false,
  defaultValue: '',
  width: '100%',
};
